import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import * as L from "lucide-react";
import api from "@/lib/api";
import RobotFace from "@/components/RobotFace";

/**
 * OnboardingFlow — 6-step magical setup shown to a new user right after signup.
 *
 * Design intent: this is Rammy's first hello. Feels like a small ritual, not a
 * form. Full-screen, warm, plenty of white space, one thing at a time. The
 * flow can be skipped at any point — we still record a "completed" timestamp
 * so it doesn't reappear.
 *
 * Props:
 *   user      : current user object (from AuthProvider)
 *   onFinish  : called after the API save succeeds (usually refresh() then close overlay)
 *   onSkip    : called after /onboarding/skip succeeds
 */
export default function OnboardingFlow({ user, onFinish, onSkip }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const firstName = (user?.name || "").split(" ")[0] || "friend";

  const [answers, setAnswers] = useState({
    preferred_name: firstName,
    voice: "warm",
    reminder_channel: "push",
    interests: [],
  });

  // Whether the user has provided enough to advance the current step.
  const canAdvance = useMemo(() => {
    switch (step) {
      case 0: return true;
      case 1: return answers.preferred_name.trim().length >= 1;
      case 2: return !!answers.voice;
      case 3: return !!answers.reminder_channel;
      case 4: return answers.interests.length >= 1;
      case 5: return true;
      default: return false;
    }
  }, [step, answers]);

  const setAns = (patch) => setAnswers((s) => ({ ...s, ...patch }));

  const finish = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding/complete", {
        preferred_name: answers.preferred_name.trim(),
        voice: answers.voice,
        reminder_channel: answers.reminder_channel,
        interests: answers.interests,
      });
      toast.success("Rammy is all set to be your companion.");
      onFinish?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding/skip", {});
      onSkip?.();
    } catch (e) {
      toast.error("Couldn't skip right now. Try once more.");
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    if (step < 5) setStep(step + 1);
    else finish();
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="fixed inset-0 z-[100] bg-[var(--rammy-bg)] flex flex-col" data-testid="onboarding-overlay">
      {/* Ambient warm glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full blur-3xl bg-[var(--rammy-cyan)]/20"/>
        <div className="absolute -bottom-32 -right-24 w-[560px] h-[560px] rounded-full blur-3xl bg-amber-200/30"/>
      </div>

      {/* Top bar: progress + skip */}
      <div className="relative z-10 flex items-center justify-between px-6 md:px-10 pt-6">
        <div className="flex items-center gap-1.5" data-testid="onboarding-progress">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i <= step
                  ? "bg-[var(--rm-ink-900)] w-8"
                  : "bg-[var(--rm-ink-900)]/15 w-4"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={skip}
          disabled={saving}
          className="text-[12px] text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] transition disabled:opacity-40"
          data-testid="onboarding-skip"
        >
          Skip for now
        </button>
      </div>

      {/* Slide content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-[560px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            >
              {step === 0 && (
                <StepWelcome firstName={firstName}/>
              )}
              {step === 1 && (
                <StepName
                  value={answers.preferred_name}
                  onChange={(v) => setAns({ preferred_name: v })}
                />
              )}
              {step === 2 && (
                <StepVoice
                  value={answers.voice}
                  onChange={(v) => setAns({ voice: v })}
                />
              )}
              {step === 3 && (
                <StepChannel
                  value={answers.reminder_channel}
                  onChange={(v) => setAns({ reminder_channel: v })}
                />
              )}
              {step === 4 && (
                <StepInterests
                  value={answers.interests}
                  onChange={(v) => setAns({ interests: v })}
                />
              )}
              {step === 5 && (
                <StepReady preferredName={answers.preferred_name}/>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Footer nav */}
      <div className="relative z-10 flex items-center justify-between px-6 md:px-10 pb-8 pt-4">
        <button
          type="button"
          onClick={back}
          disabled={step === 0 || saving}
          className="text-[13px] text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)] transition disabled:opacity-30"
          data-testid="onboarding-back"
        >
          Back
        </button>
        <button
          type="button"
          onClick={next}
          disabled={!canAdvance || saving}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[var(--rm-ink-900)] text-white text-[13px] font-medium hover:opacity-90 transition disabled:opacity-40 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.35)]"
          data-testid="onboarding-next"
        >
          {saving ? "Saving..." : step === 5 ? "Let's start" : step === 0 ? "Let's go" : "Continue"}
          <L.ArrowRight size={14}/>
        </button>
      </div>
    </div>
  );
}

/* ── Individual step slides ─────────────────────────────────────────────── */

function StepWelcome({ firstName }) {
  return (
    <div className="text-center">
      <div className="relative mx-auto w-28 h-28 md:w-32 md:h-32 rounded-[2rem] bg-[var(--rm-ink-900)] flex items-center justify-center ring-2 ring-[var(--rammy-cyan)]/40 shadow-[0_0_60px_-10px_var(--rammy-cyan)] mb-6">
        <RobotFace expression="idle" size={110} showHud={false}/>
        <div className="absolute inset-0 rounded-[2rem] bg-[var(--rammy-cyan)]/30 blur-2xl -z-10 animate-pulse"/>
      </div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rammy-cyan-dark)] font-semibold">
        Hi {firstName}
      </div>
      <h1 className="mt-3 text-3xl md:text-4xl font-display text-[var(--rammy-text)] leading-tight">
        Let&apos;s set up your Rammy.
      </h1>
      <p className="mt-3 text-[14px] md:text-[15px] text-[var(--rammy-text-sec)] leading-relaxed max-w-[440px] mx-auto">
        Six quick questions — takes about a minute. It helps me feel less like an app and more like <em>your</em> companion.
      </p>
    </div>
  );
}

function StepName({ value, onChange }) {
  return (
    <div className="text-center">
      <StepHeader
        kicker="Step 1 of 4"
        title="What should I call you?"
        subtitle="Use any name you like — a nickname is perfect."
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Sam"
        maxLength={40}
        autoFocus
        className="mt-8 w-full max-w-[360px] mx-auto block text-center text-2xl md:text-3xl font-display bg-transparent border-b-2 border-[var(--rammy-text)]/20 focus:border-[var(--rammy-cyan)] outline-none pb-2 text-[var(--rammy-text)]"
        data-testid="onboarding-preferred-name"
      />
    </div>
  );
}

function StepVoice({ value, onChange }) {
  const options = [
    { id: "warm",    icon: <L.Heart size={16}/>,   title: "Warm",    hint: "Kind, unhurried, human." },
    { id: "playful", icon: <L.Sparkles size={16}/>,title: "Playful", hint: "Curious and a little witty." },
    { id: "calm",    icon: <L.Leaf size={16}/>,    title: "Calm",    hint: "Grounded and unrushed." },
    { id: "focused", icon: <L.Target size={16}/>,  title: "Focused", hint: "Crisp, direct, useful." },
  ];
  return (
    <div className="text-center">
      <StepHeader
        kicker="Step 2 of 4"
        title="Pick my vibe."
        subtitle="Sets how I talk to you day-to-day. You can change this anytime."
      />
      <div className="mt-8 grid grid-cols-2 gap-3" data-testid="onboarding-voice-grid">
        {options.map((o) => {
          const active = value === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onChange(o.id)}
              data-testid={`onboarding-voice-${o.id}`}
              className={`text-left rounded-2xl p-4 border transition-all ${
                active
                  ? "bg-[var(--rm-ink-900)] text-white border-[var(--rammy-text)] shadow-[0_12px_32px_-12px_rgba(17,19,24,0.5)]"
                  : "bg-[var(--rm-surface)]/70 border-[var(--rammy-text)]/10 hover:border-[var(--rammy-cyan)]/60 text-[var(--rammy-text)]"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                  active ? "bg-[var(--rammy-cyan)]/30 text-[var(--rammy-cyan)]" : "bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)]"
                }`}>
                  {o.icon}
                </span>
                <span className="text-[14px] font-semibold">{o.title}</span>
              </div>
              <div className={`text-[12px] mt-1.5 leading-snug ${active ? "text-white/70" : "text-[var(--rammy-text-sec)]"}`}>
                {o.hint}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepChannel({ value, onChange }) {
  const options = [
    { id: "push",     icon: <L.BellRing size={16}/>, title: "Push",     hint: "Right here in the browser or on your phone." },
    { id: "email",    icon: <L.Mail size={16}/>,     title: "Email",    hint: "A gentle nudge in your inbox." },
    { id: "telegram", icon: <L.Send size={16}/>,     title: "Telegram", hint: "Straight to your Telegram chat." },
  ];
  return (
    <div className="text-center">
      <StepHeader
        kicker="Step 3 of 4"
        title="How should I nudge you?"
        subtitle="For reminders, briefings, and follow-ups."
      />
      <div className="mt-8 flex flex-col gap-3" data-testid="onboarding-channel-list">
        {options.map((o) => {
          const active = value === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onChange(o.id)}
              data-testid={`onboarding-channel-${o.id}`}
              className={`flex items-center gap-3 text-left rounded-2xl p-4 border transition-all ${
                active
                  ? "bg-[var(--rm-ink-900)] text-white border-[var(--rammy-text)]"
                  : "bg-[var(--rm-surface)]/70 border-[var(--rammy-text)]/10 hover:border-[var(--rammy-cyan)]/60 text-[var(--rammy-text)]"
              }`}
            >
              <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                active ? "bg-[var(--rammy-cyan)]/30 text-[var(--rammy-cyan)]" : "bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)]"
              }`}>
                {o.icon}
              </span>
              <div className="flex-1">
                <div className="text-[14px] font-semibold">{o.title}</div>
                <div className={`text-[12px] mt-0.5 ${active ? "text-white/70" : "text-[var(--rammy-text-sec)]"}`}>
                  {o.hint}
                </div>
              </div>
              {active && <L.Check size={16} className="text-[var(--rammy-cyan)]"/>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepInterests({ value, onChange }) {
  const options = [
    { id: "work",       icon: <L.Briefcase size={16}/>,  title: "Work" },
    { id: "health",     icon: <L.Activity size={16}/>,   title: "Health" },
    { id: "learning",   icon: <L.BookOpen size={16}/>,   title: "Learning" },
    { id: "creativity", icon: <L.Palette size={16}/>,    title: "Creativity" },
    { id: "family",     icon: <L.Users size={16}/>,      title: "Family" },
  ];
  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((x) => x !== id));
    else onChange([...value, id]);
  };
  return (
    <div className="text-center">
      <StepHeader
        kicker="Step 4 of 4"
        title="What can I help you with?"
        subtitle="Pick one or two — I'll weave these into everyday nudges."
      />
      <div className="mt-8 flex flex-wrap justify-center gap-2.5" data-testid="onboarding-interests-grid">
        {options.map((o) => {
          const active = value.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => toggle(o.id)}
              data-testid={`onboarding-interest-${o.id}`}
              className={`inline-flex items-center gap-2 h-11 px-4 rounded-full border transition-all ${
                active
                  ? "bg-[var(--rm-ink-900)] text-white border-[var(--rammy-text)] shadow-[0_10px_30px_-12px_rgba(17,19,24,0.4)]"
                  : "bg-[var(--rm-surface)]/70 border-[var(--rammy-text)]/10 hover:border-[var(--rammy-cyan)]/60 text-[var(--rammy-text)]"
              }`}
            >
              <span className={active ? "text-[var(--rammy-cyan)]" : "text-[var(--rammy-cyan-dark)]"}>{o.icon}</span>
              <span className="text-[13px] font-medium">{o.title}</span>
              {active && <L.Check size={14} className="text-[var(--rammy-cyan)]"/>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepReady({ preferredName }) {
  return (
    <div className="text-center">
      <div className="relative mx-auto w-28 h-28 md:w-32 md:h-32 rounded-[2rem] bg-[var(--rm-ink-900)] flex items-center justify-center ring-2 ring-[var(--rammy-cyan)]/40 shadow-[0_0_60px_-10px_var(--rammy-cyan)] mb-6">
        <RobotFace expression="happy" size={110} showHud={false}/>
        <div className="absolute inset-0 rounded-[2rem] bg-[var(--rammy-cyan)]/40 blur-2xl -z-10 animate-pulse"/>
      </div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rammy-cyan-dark)] font-semibold">
        All set
      </div>
      <h1 className="mt-3 text-3xl md:text-4xl font-display text-[var(--rammy-text)] leading-tight">
        Nice to meet you, {preferredName}.
      </h1>
      <p className="mt-3 text-[14px] md:text-[15px] text-[var(--rammy-text-sec)] leading-relaxed max-w-[440px] mx-auto">
        I&apos;m Rammy. From here on I&apos;ll remember what matters to you and keep your day flowing. Let&apos;s start.
      </p>
    </div>
  );
}

function StepHeader({ kicker, title, subtitle }) {
  return (
    <>
      <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rammy-cyan-dark)] font-semibold">
        {kicker}
      </div>
      <h1 className="mt-3 text-2xl md:text-3xl font-display text-[var(--rammy-text)] leading-tight">
        {title}
      </h1>
      {subtitle && (
        <p className="mt-2 text-[13px] md:text-[14px] text-[var(--rammy-text-sec)] leading-relaxed max-w-[440px] mx-auto">
          {subtitle}
        </p>
      )}
    </>
  );
}
